import { user } from 'src/StateObserver';
import { beginUserWait } from 'src/lib/analytics/gameLoadTracking';
import { GCInstant } from 'src/lib/gcinstant';
import { requestOfficialAccountFriend } from '../chatbot/chatbot.api';
import { showIncentivizedOAHalfModal } from '../chatbot/incentivizedOA.sequence';
import {
  hasProfileConsent,
  isProfileConsentSupported,
  requestProfileConsent,
} from '../profile/profile.api';
import { type ConsentSurface } from './onboarding.analytics';
import { isInLineMiniApp } from '@play-co/gcinstant';
import { incentivizedOAStartLevel } from 'src/replicant/chatbot/chatbot.ruleset';

/**
 * Run the first-entry LINE onboarding flow on the splash screen.
 *
 * @param origin Analytics surface for the profile-consent prompt.
 * @returns A promise that resolves after the optional prompt closes.
 */
export async function onboardingFirstEntry(
  origin: ConsentSurface,
): Promise<void> {
  // Measure the complete first-entry onboarding flow.
  const endUserWait = beginUserWait();

  try {
    // First entry only prompts when soft profile consent is supported and missing.
    if (!isProfileConsentSupported() || hasProfileConsent()) return;

    // Show the optional profile-consent prompt while the loading screen is visible.
    await requestProfileConsent(origin);
  } finally {
    // Log onboarding time so it can be removed from the load duration at splash dismissal.
    endUserWait();
  }
}

/**
 * Run the returning-user LINE onboarding flow during the launch sequence.
 *
 * @param origin Analytics surface for the onboarding prompt.
 * @returns A promise that resolves after the applicable prompt closes.
 */
export async function onboardingReturning(
  origin: ConsentSurface,
): Promise<void> {
  // Measure the complete returning-user onboarding flow.
  const endUserWait = beginUserWait();

  try {
    const currentUser = user();
    const shouldUseIncentivizedOAAddition =
      isInLineMiniApp() &&
      currentUser.match2.lastLevel >= incentivizedOAStartLevel;
    if (shouldUseIncentivizedOAAddition) {
      await showIncentivizedOAHalfModal('launch_sequence');
      return;
    }

    // Returning users must finish soft profile consent before Add Friend is shown.
    if (isProfileConsentSupported() && !hasProfileConsent()) {
      await requestProfileConsent(origin);
      return;
    }

    // Existing OA friends do not need an Add Friend prompt.
    if (GCInstant.isSubscribedToBot) return;

    // Ask unsubscribed users to add the OA using the standard client-side check.
    await requestOfficialAccountFriend(origin);
  } finally {
    // Log onboarding time so it can be removed from the load duration at splash dismissal.
    endUserWait();
  }
}
